import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { displayFileName } from '@/lib/utils';
import { useRealtimeWorkflows, type ExecutionStatus, type ExecutionOutput } from '@/hooks/useRealtimeWorkflows';
import OutputViewer, { getOutputKind, type OutputKind } from '@/components/OutputViewer';
import {
  ArrowLeft,
  Loader2,
  CheckCircle,
  XCircle,
  Download,
  RotateCcw,
  Image,
  Video,
  Copy,
  Check,
  FileUp,
  FileText,
  File as FileIcon,
  ChevronDown,
  ChevronUp,
  Clock,
  Maximize2,
} from 'lucide-react';

type InputMeta = Record<string, { nodeName: string; label: string }>;

const INPUTS_COLLAPSED_COUNT = 5;

function formatInputValue(value: unknown): string {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
}

function formatExecutedAt(iso: string): string {
  const date = new Date(iso);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export default function ExecutionDetailPage() {
  const { promptId } = useParams<{ promptId: string }>();
  const { executions, getInputUrl, getWorkflow, refreshExecutionOutputs } = useRealtimeWorkflows();

  const [status, setStatus] = useState<ExecutionStatus>({ status: 'queued' });
  const [workflowName, setWorkflowName] = useState('');
  const [createdAt, setCreatedAt] = useState('');
  const [inputMeta, setInputMeta] = useState<InputMeta>({});
  const [inputParams, setInputParams] = useState<Record<string, unknown>>({});
  const [inputFileNames, setInputFileNames] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [errorDetails, setErrorDetails] = useState('');
  const [errorExpanded, setErrorExpanded] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inputsExpanded, setInputsExpanded] = useState(false);
  const [viewerIndex, setViewerIndex] = useState<number | null>(null);
  // A single uploaded input opened in the fullscreen viewer (separate from the
  // outputs carousel, which navigates through the outputs array).
  const [inputViewer, setInputViewer] = useState<ExecutionOutput | null>(null);
  const [isWorkflowDeleted, setIsWorkflowDeleted] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [pdfError, setPdfError] = useState('');

  const execRecord = useMemo(() => executions.find((e) => e.id === promptId), [executions, promptId]);

  const handleReviewPdf = useCallback(async () => {
    if (!execRecord || pdfBusy) return;
    setPdfBusy(true);
    setPdfError('');
    try {
      const { downloadReviewPdf } = await import('@/lib/reviewDoc');
      await downloadReviewPdf(execRecord);
    } catch (err) {
      setPdfError(err instanceof Error ? err.message : 'Failed to generate the review document');
    } finally {
      setPdfBusy(false);
    }
  }, [execRecord, pdfBusy]);

  // Real-time monitoring via Firestore listener
  useEffect(() => {
    if (!promptId) return;

    const currentExec = executions.find((e) => e.id === promptId);
    if (!currentExec) return;

    const newStatus: ExecutionStatus = {
      status: currentExec.status as ExecutionStatus['status'],
      workflowId: currentExec.workflowId,
      outputs: currentExec.outputs,
      error: currentExec.error,
    };

    setStatus(newStatus);
    setWorkflowName(currentExec.workflowName || '');
    setCreatedAt(currentExec.createdAt || '');
    setInputMeta(currentExec.inputMeta || {});
    setInputParams(currentExec.inputParams || {});
    setInputFileNames(currentExec.inputFileNames || {});
    setErrorDetails(currentExec.errorDetails || '');

    if (currentExec.error) {
      setError(currentExec.error);
    }
  }, [promptId, executions]);

  // On open, ask the backend to re-sign any expired output URLs (persisted to
  // Firestore, so the realtime listener then delivers the fresh URLs). Runs once
  // per execution to avoid a request on every listener tick.
  const refreshedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!promptId || refreshedRef.current === promptId) return;
    if (status.status !== 'completed') return;
    refreshedRef.current = promptId;
    refreshExecutionOutputs(promptId);
  }, [promptId, status.status, refreshExecutionOutputs]);

  // Check if the workflow has been deleted
  useEffect(() => {
    const checkWorkflowStatus = async () => {
      if (!status.workflowId) {
        setIsWorkflowDeleted(false);
        return;
      }

      try {
        await getWorkflow(status.workflowId);
        setIsWorkflowDeleted(false);
      } catch (err) {
        // If workflow fetch fails with 404, it's been deleted
        setIsWorkflowDeleted(true);
        console.log('Workflow has been deleted:', status.workflowId);
      }
    };

    checkWorkflowStatus();
  }, [status.workflowId, getWorkflow]);

  // User-provided inputs grouped by node. Only fields the user could actually
  // edit are stored, except filename_prefix which the backend overwrites with
  // an auto-generated value — so hide it. Node names and field labels come from
  // the inputMeta snapshot saved at execution time (survives workflow deletion);
  // the stored key is "<nodeId>.<inputName>".
  const inputGroups = useMemo(() => {
    const inputName = (key: string) => key.split('.').slice(1).join('.') || key;

    // Node name from the snapshot, else a "Node <id>" fallback.
    const nodeNameFor = (key: string) => {
      const fromMeta = inputMeta[key]?.nodeName?.trim();
      if (fromMeta) return fromMeta;
      const nodeId = key.split('.')[0];
      return nodeId ? `Node ${nodeId}` : 'Inputs';
    };
    // Field label from the snapshot, else the raw input name.
    const fieldLabelFor = (key: string) => inputMeta[key]?.label?.trim() || inputName(key);

    type Entry = { key: string; label: string; value: string; isFile: boolean; previewUrl?: string; kind?: OutputKind };
    const groups = new Map<string, { node: string; entries: Entry[] }>();
    const add = (key: string, value: string, isFile: boolean) => {
      const node = nodeNameFor(key);
      if (!groups.has(node)) groups.set(node, { node, entries: [] });
      const entry: Entry = { key, label: fieldLabelFor(key), value, isFile };
      // For uploaded image/video inputs, resolve a servable URL for a thumbnail.
      if (isFile && promptId) {
        const kind = getOutputKind(value);
        if (kind === 'image' || kind === 'video') {
          entry.kind = kind;
          entry.previewUrl = getInputUrl(promptId, value);
        }
      }
      groups.get(node)!.entries.push(entry);
    };

    for (const [key, value] of Object.entries(inputFileNames)) add(key, value, true);
    for (const [key, value] of Object.entries(inputParams)) add(key, formatInputValue(value), false);

    return [...groups.values()].filter((g) => g.entries.length > 0);
  }, [inputParams, inputFileNames, inputMeta, promptId, getInputUrl]);

  const hasInputs = inputGroups.length > 0;
  const totalInputCount = useMemo(() => inputGroups.reduce((sum, g) => sum + g.entries.length, 0), [inputGroups]);

  // When collapsed, show only the first N input entries (counted across groups),
  // preserving the node grouping for whatever entries remain visible.
  const visibleInputGroups = useMemo(() => {
    if (inputsExpanded || totalInputCount <= INPUTS_COLLAPSED_COUNT) return inputGroups;
    let remaining = INPUTS_COLLAPSED_COUNT;
    const result: typeof inputGroups = [];
    for (const group of inputGroups) {
      if (remaining <= 0) break;
      const entries = group.entries.slice(0, remaining);
      remaining -= entries.length;
      result.push({ ...group, entries });
    }
    return result;
  }, [inputGroups, inputsExpanded, totalInputCount]);

  const canCollapseInputs = totalInputCount > INPUTS_COLLAPSED_COUNT;

  // Display/download names with the backend's anti-collision segment stripped,
  // keyed by the original filename. The stored filename/URL is unchanged.
  const downloadNames = useMemo(() => {
    const map: Record<string, string> = {};
    for (const output of status.outputs || []) {
      map[output.filename] = displayFileName(output.filename);
    }
    return map;
  }, [status.outputs]);

  const outputs = useMemo(() => (status.outputs || []).filter((o) => !o.hidden), [status.outputs]);

  const isProcessing = status.status === 'queued' || status.status === 'running' || status.status === 'localizing';

  const handleCopyId = () => {
    if (promptId) {
      navigator.clipboard.writeText(promptId);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleDownload = async (url: string, downloadName: string) => {
    // Fetch the signed bucket URL as a blob so it downloads directly under the
    // chosen name instead of navigating to the bucket's inline view.
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Download failed: ${response.status}`);
      const blob = await response.blob();
      const blobUrl = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = downloadName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Download failed:', error);
    }
  };

  const closeViewer = useCallback(() => setViewerIndex(null), []);
  const showPrev = useCallback(
    () => setViewerIndex((i) => (i === null ? i : (i - 1 + outputs.length) % outputs.length)),
    [outputs.length],
  );
  const showNext = useCallback(() => setViewerIndex((i) => (i === null ? i : (i + 1) % outputs.length)), [outputs.length]);

  // Keyboard navigation while the outputs viewer is open.
  useEffect(() => {
    if (viewerIndex === null) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeViewer();
      else if (e.key === 'ArrowLeft') showPrev();
      else if (e.key === 'ArrowRight') showNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewerIndex, closeViewer, showPrev, showNext]);

  // Escape closes the single-input viewer.
  useEffect(() => {
    if (!inputViewer) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setInputViewer(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [inputViewer]);

  return (
    <div className="h-full bg-gradient-to-br from-background via-background to-accent/30 dark:to-accent/10 p-8 overflow-auto">
      <div className="max-w-6xl mx-auto">
        <header className="mb-8">
          <Link to="/history" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4 w-fit">
            <ArrowLeft className="h-3 w-3" />
            Back to History
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-foreground">Execution Details</h1>
              {workflowName && <p className="text-sm text-muted-foreground mt-1">{workflowName}</p>}
              {createdAt && formatExecutedAt(createdAt) && (
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1.5">
                  <Clock className="h-3 w-3" />
                  {formatExecutedAt(createdAt)}
                </p>
              )}
            </div>
            <button
              onClick={handleCopyId}
              className={
                'flex items-center gap-1.5 text-xs font-mono ' +
                'text-muted-foreground hover:text-foreground ' +
                'bg-accent/50 px-2.5 py-1 rounded-md transition-colors'
              }
            >
              {copied ? <Check className="h-3 w-3 text-green-500" /> : <Copy className="h-3 w-3" />}
              {promptId?.slice(0, 8)}...
            </button>
          </div>
        </header>

        {/* Status */}
        <Card className="mb-6 border-border/60">
          <CardHeader>
            <div className="flex items-center justify-between gap-3">
              <CardTitle className="flex items-center gap-3">
                {isProcessing && <Loader2 className="h-6 w-6 animate-spin text-violet-500" />}
                {status.status === 'completed' && <CheckCircle className="h-6 w-6 text-green-500" />}
                {status.status === 'error' && <XCircle className="h-6 w-6 text-red-500" />}
                {status.status === 'cancelled' && <XCircle className="h-6 w-6 text-muted-foreground" />}
                {status.status === 'queued' && 'Queued...'}
                {status.status === 'running' && 'Processing...'}
                {status.status === 'localizing' && 'Receiving assets...'}
                {status.status === 'completed' && 'Completed'}
                {status.status === 'error' && 'Failed'}
                {status.status === 'cancelled' && 'Cancelled'}
                {status.status === 'unknown' && 'Checking status...'}
              </CardTitle>
              {(status.status === 'completed' || status.status === 'error' || status.status === 'cancelled') && (
                <>
                  {isWorkflowDeleted ? (
                    <Button
                      size="sm"
                      disabled
                      className="bg-muted text-muted-foreground cursor-not-allowed"
                      title="Workflow has been deleted"
                    >
                      <RotateCcw className="h-4 w-4 mr-2" />
                      Run Again
                    </Button>
                  ) : (
                    <Link to={status.workflowId ? `/run/${status.workflowId}?from=${promptId}` : '/'}>
                      <Button size="sm" className="bg-violet-600 hover:bg-violet-700 text-white">
                        <RotateCcw className="h-4 w-4 mr-2" />
                        Run Again
                      </Button>
                    </Link>
                  )}
                </>
              )}
            </div>
          </CardHeader>
          {isProcessing && (
            <CardContent>
              <div className="space-y-3">
                <div className="w-full bg-accent rounded-full h-2 overflow-hidden">
                  <div className="bg-violet-500 h-2 rounded-full animate-pulse w-2/3" />
                </div>
                <p className="text-sm text-muted-foreground">
                  {status.status === 'queued'
                    ? 'Waiting in the queue...'
                    : status.status === 'localizing'
                      ? 'Receiving output files'
                      : 'Your workflow is being processed by AICS Workflow Runner'}
                </p>
              </div>
            </CardContent>
          )}
          {(status.status === 'error' || error) && (
            <CardContent>
              <div className="rounded-lg border border-destructive/30 bg-destructive/10 p-3 space-y-2">
                <p className="text-sm text-destructive break-words">
                  {status.error || error || 'An error occurred during execution'}
                </p>
                {errorDetails && (
                  <div>
                    <button
                      type="button"
                      onClick={() => setErrorExpanded((v) => !v)}
                      className="flex items-center gap-1 text-xs font-medium text-destructive hover:underline"
                    >
                      {errorExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                      {errorExpanded ? 'Hide full error' : 'Show full error'}
                    </button>
                    {errorExpanded && (
                      <pre
                        className={
                          'mt-2 text-xs text-left text-destructive/90 bg-background/60 ' +
                          'border border-destructive/20 rounded-md p-3 overflow-auto max-h-80 ' +
                          'whitespace-pre-wrap break-words font-mono'
                        }
                      >
                        {errorDetails}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            </CardContent>
          )}
        </Card>

        {/* Inputs (left) + Outputs (right) */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6 items-start mb-6">
          {/* Inputs used for this execution, grouped by node */}
          {hasInputs && (
            <Card className="border-border/60 lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Inputs</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-5">
                  {visibleInputGroups.map((group) => (
                    <div key={group.node}>
                      <div className="text-sm font-semibold text-foreground mb-2">{group.node}</div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                        {group.entries.map((entry) => (
                          <div key={entry.key} className="rounded-lg border border-border/60 bg-accent/20 px-3 py-2">
                            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide flex items-center gap-1.5">
                              {entry.isFile && <FileUp className="h-3 w-3" />}
                              {entry.label}
                            </div>
                            {entry.previewUrl && entry.kind === 'image' && (
                              <button
                                type="button"
                                title="Open"
                                onClick={() =>
                                  setInputViewer({ nodeId: '', filename: entry.value, url: entry.previewUrl!, type: 'image' })
                                }
                                className="mt-2 block w-full cursor-pointer"
                              >
                                <img
                                  src={entry.previewUrl}
                                  alt={entry.value}
                                  className={
                                    'w-full max-h-40 object-contain rounded-md border border-border/60 ' +
                                    'bg-background hover:border-violet-500/50 transition-colors'
                                  }
                                />
                              </button>
                            )}
                            {entry.previewUrl && entry.kind === 'video' && (
                              <div className="mt-2 relative">
                                <video
                                  src={entry.previewUrl}
                                  controls
                                  className="w-full max-h-40 rounded-md border border-border/60 bg-background"
                                />
                                <button
                                  type="button"
                                  title="Open fullscreen"
                                  onClick={() =>
                                    setInputViewer({ nodeId: '', filename: entry.value, url: entry.previewUrl!, type: 'video' })
                                  }
                                  className={
                                    'absolute top-1 right-1 p-1.5 rounded-md bg-black/50 text-white ' +
                                    'hover:bg-black/70 backdrop-blur-sm transition-colors'
                                  }
                                >
                                  <Maximize2 className="h-3.5 w-3.5" />
                                </button>
                              </div>
                            )}
                            <div className="text-sm text-foreground mt-1 break-words">{entry.value}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
                {canCollapseInputs && (
                  <button
                    type="button"
                    onClick={() => setInputsExpanded((v) => !v)}
                    className={
                      'mt-4 w-full flex items-center justify-center gap-1 pt-3 border-t border-border/60 ' +
                      'text-sm font-medium text-violet-600 dark:text-violet-400 hover:text-violet-700 transition-colors'
                    }
                  >
                    {inputsExpanded ? (
                      <>
                        <ChevronUp className="h-4 w-4" />
                        Show less
                      </>
                    ) : (
                      <>
                        <ChevronDown className="h-4 w-4" />
                        Show all {totalInputCount} inputs
                      </>
                    )}
                  </button>
                )}
              </CardContent>
            </Card>
          )}

          {/* Outputs */}
          {status.status === 'completed' && status.outputs && status.outputs.length > 0 && (
            <Card className={'border-border/60 ' + (hasInputs ? 'lg:col-span-3' : 'lg:col-span-5')}>
              <CardHeader>
                <div className="flex items-center justify-between gap-3">
                  <CardTitle className="text-lg">Results</CardTitle>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleReviewPdf}
                    disabled={pdfBusy || !execRecord}
                    title="Download a review document (PDF) with the outputs, prompts, and run metadata"
                  >
                    {pdfBusy ? (
                      <>
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        Preparing document…
                      </>
                    ) : (
                      <>
                        <FileText className="h-4 w-4 mr-2" />
                        Legal review PDF
                      </>
                    )}
                  </Button>
                </div>
                {pdfError && <p className="text-xs text-destructive mt-1">{pdfError}</p>}
              </CardHeader>
              <CardContent>
                <div className={outputs.length === 1 ? 'flex justify-center' : 'grid grid-cols-1 sm:grid-cols-2 gap-4'}>
                  {outputs.map((output, i) => {
                    const kind = getOutputKind(output.filename);
                    const name = downloadNames[output.filename] || output.filename;

                    return (
                      <div
                        key={i}
                        className={
                          'group relative rounded-xl border border-border/60 overflow-hidden ' +
                          'cursor-pointer hover:border-violet-500/50 transition-colors ' +
                          (outputs.length === 1 ? 'w-full max-w-md' : '')
                        }
                        onClick={() => setViewerIndex(i)}
                      >
                        {/* Download icon in the top-right corner */}
                        <button
                          type="button"
                          title="Download"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDownload(output.url, name);
                          }}
                          className={
                            'absolute top-2 right-2 z-10 p-2 rounded-lg ' +
                            'bg-black/50 text-white hover:bg-black/70 backdrop-blur-sm transition-colors'
                          }
                        >
                          <Download className="h-4 w-4" />
                        </button>

                        {kind === 'image' && <img src={output.url} alt={name} className="w-full aspect-square object-cover" />}
                        {kind === 'video' && <video src={output.url} className="w-full aspect-square object-cover" muted />}
                        {kind !== 'image' && kind !== 'video' && (
                          <div className="w-full aspect-square bg-accent/30 flex items-center justify-center">
                            {kind === 'text' ? (
                              <FileText className="h-12 w-12 text-muted-foreground/40" />
                            ) : (
                              <FileIcon className="h-12 w-12 text-muted-foreground/40" />
                            )}
                          </div>
                        )}

                        <div className="flex items-center gap-2 px-3 py-2 bg-accent/30">
                          {kind === 'image' && <Image className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                          {kind === 'video' && <Video className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                          {kind === 'text' && <FileText className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                          {kind === 'other' && <FileIcon className="h-4 w-4 flex-shrink-0 text-muted-foreground" />}
                          <span className="text-sm text-muted-foreground truncate">{name}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Multimedia viewer modal */}
      {viewerIndex !== null && outputs[viewerIndex] && (
        <OutputViewer
          output={outputs[viewerIndex]}
          name={downloadNames[outputs[viewerIndex].filename] || outputs[viewerIndex].filename}
          index={viewerIndex}
          total={outputs.length}
          onClose={closeViewer}
          onPrev={showPrev}
          onNext={showNext}
          onDownload={handleDownload}
        />
      )}

      {/* Single uploaded input opened fullscreen (no carousel) */}
      {inputViewer && (
        <OutputViewer
          output={inputViewer}
          name={inputViewer.filename}
          index={0}
          total={1}
          onClose={() => setInputViewer(null)}
          onPrev={() => {}}
          onNext={() => {}}
          onDownload={handleDownload}
        />
      )}
    </div>
  );
}
